// Выбор цели атаки: фракционные приоритеты + taunt-redirect + stealth-skip.
//
// selectAttackTarget — единая точка для авто-атаки (Фаза 3) и ручной атаки Героем.
// Чистая функция, не пишет в state — события возвращаются в результате,
// а вызывающий код пишет их в лог.

import { hasKeyword, KW } from './keywords.js';
import { pickGlobalTarget } from './abilities/global/priority.js';

// Фракционные дефолты приоритета.
// Б.1.a: Синтетикс — random («киберпсихи»).
// В.2.a: Лив Лайф — lowest_current_hp («самый раненый», диздок 7.4).
//        Это минимум по ТЕКУЩЕМУ hp (не max). Не путать с дефолтом Деуса
//        ('weakest') — там минимум по max_hp.
// Override на самой карте побеждает дефолт фракции (см. getPriorityForUnit).
const DEFAULTS_BY_FACTION = {
  deus: 'weakest',
  syntetics: 'random',
  global: 'weakest',
  livlife: 'lowest_current_hp',
  neutral: 'weakest'
};

// Эффективный приоритет данного юнита (override карты или дефолт фракции).
// В.1.a: для юнитов Глобала с выбранным attackPriority — он побеждает override.
export function getPriorityForUnit(unit) {
  if (unit?.faction === 'global' && unit.attackPriority) return unit.attackPriority;
  return unit.priority_override || DEFAULTS_BY_FACTION[unit.faction] || 'weakest';
}

// Возвращает результат выбора цели:
//   { kind: 'unit', target: Card,        redirectedToTaunt, stealthSkipped }
//   { kind: 'hero', target: Card (hero), redirectedToTaunt: false, stealthSkipped: [] }
//   { kind: 'skip', target: null, reason: 'all_stealth' | 'no_target', stealthSkipped }
//
// Логика (В.2.f-2-fix1: Скрытность побеждает Провокацию):
//  1. Если у врага живых юнитов нет — цель = Герой (если жив).
//  2. Сначала отфильтровать ВСЕ стелс-юниты (они «невидимы» — независимо
//     от taunt). Стелс-taunt НЕ выбирается ни как taunt-цель, ни как обычная
//     цель: «стелс невидим» — единственная непротиворечивая интерпретация
//     одновременных stealth+taunt.
//  3. Если среди ОСТАВШИХСЯ есть taunt-юнит(ы) — выбираем самого левого
//     в первой линии, при отсутствии — во второй (redirectedToTaunt).
//  4. Иначе — visible = не-стелс и не Спящий агент. Если visible пусто —
//     все живые враги стелс → атака идёт в Героя.
//  5. Применить фракционный приоритет к visible.
export function selectAttackTarget(attacker, enemyPlayer, rng) {
  // А.4.4.4: Спящий агент той же фракции не может быть атакован, когда на стороне противника.
  // C.1 Этап 33: пока спит (sleeperState='planted'/'armed') — НЕ выбирается
  // авто-атакой никем (диздок 7.1: карта-бомба, не обычный боец). До фикса
  // other-faction юниты могли его бить → Спящий получал урон в Ф3.
  // На стороне противника = card.side === 'opponent'.
  const allLiving = enemyPlayer.unitsOnField();
  const living = allLiving.filter(u => {
    if (u.id !== 'deus_inf_spyashy_agent') return true;
    // Спящий-бомба (planted/armed) — невидим для авто-атак.
    if (u.sleeperState === 'planted' || u.sleeperState === 'armed') return false;
    if (u.side !== 'opponent') return true;
    return u.faction !== attacker.faction;
  });

  if (living.length === 0) {
    if (enemyPlayer.hero.hp > 0) {
      return {
        kind: 'hero',
        target: enemyPlayer.hero,
        redirectedToTaunt: false,
        stealthSkipped: []
      };
    }
    return {
      kind: 'skip',
      target: null,
      reason: 'no_target',
      redirectedToTaunt: false,
      stealthSkipped: []
    };
  }

  // В.2.f-2-fix1: Скрытность побеждает Провокацию. Сначала фильтруем
  // ВСЕ стелс-юниты — они «невидимы», даже если у них taunt. Стелс-taunt
  // НЕ становится перенаправлением.
  const stealthSkipped = living.filter(u => hasKeyword(u, KW.STEALTH));
  const nonStealth = living.filter(u => !hasKeyword(u, KW.STEALTH));

  // Taunt среди НЕ-стелс юнитов: redirect.
  const taunts = nonStealth.filter(u => hasKeyword(u, KW.TAUNT));
  // visible = не-стелс и не Спящий агент (для Авангарда).
  const visible = nonStealth
    .filter(u => u.id !== 'deus_inf_spyashy_agent');    // А.4.4.4: spy не считается для Авангарда
  if (taunts.length > 0) {
    // АНИМ.7-fix-redirect: originalTarget — куда атакующий пошёл бы
    // БЕЗ Provocation. Для UI визуализации redirect-стрелки. Если есть
    // не-taunt visible — самый левый, иначе — Hero.
    const nonTauntVisible = visible.filter(u => !hasKeyword(u, KW.TAUNT));
    const originalTarget = nonTauntVisible.length > 0
      ? pickByPosition(nonTauntVisible)
      : enemyPlayer.hero;
    return {
      kind: 'unit',
      target: pickByPosition(taunts),
      originalTarget,
      redirectedToTaunt: true,
      stealthSkipped
    };
  }

  if (visible.length === 0) {
    // Все живые юниты — стелс. Они НЕ держат Авангард: атака идёт в Героя.
    if (enemyPlayer.hero.hp > 0) {
      return {
        kind: 'hero',
        target: enemyPlayer.hero,
        redirectedToTaunt: false,
        stealthSkipped
      };
    }
    // Защитный фолбэк: нет видимых юнитов и Герой мёртв — теоретически
    // не должен наступать (в этот момент матч уже закончен).
    return {
      kind: 'skip',
      target: null,
      reason: 'no_target',
      redirectedToTaunt: false,
      stealthSkipped
    };
  }

  return {
    kind: 'unit',
    target: applyPriority(attacker, visible, rng),
    redirectedToTaunt: false,
    stealthSkipped
  };
}

// Совместимость с предыдущим API: возвращает только Card | null.
export function chooseTargetForUnit(attacker, enemyPlayer, rng) {
  const r = selectAttackTarget(attacker, enemyPlayer, rng);
  return r.target;
}

// Выбор «первый слева в линии 1, потом в линии 2».
function pickByPosition(units) {
  const sorted = [...units].sort((a, b) => {
    if (a.line !== b.line) return a.line - b.line;
    return a.slot - b.slot;
  });
  return sorted[0];
}

function applyPriority(attacker, pool, rng) {
  // В.1.a / БУГ.3: Глобал с выбранным игроком приоритетом — отдельный picker
  // (4 ветки strongest/weakest/healer/random + tie-break по позиции).
  if (attacker?.faction === 'global' && attacker.attackPriority) {
    return pickGlobalTarget(attacker, pool, rng);
  }
  // БУГ.3: Глобал без attackPriority (бот, sandbox, не задано) — random
  // как самый безопасный фолбэк. Игрок-человек ставит выбор через UI.
  if (attacker?.faction === 'global') {
    return pool[(rng?.int && typeof rng.int === 'function')
      ? rng.int(pool.length)
      : Math.floor(Math.random() * pool.length)];
  }
  const priority = getPriorityForUnit(attacker);
  switch (priority) {
    case 'strongest':
      return pickByMinMax(pool, c => c.maxHp, /*max=*/ true);
    case 'wounded': {
      const wounded = pool.filter(c => c.hp < c.maxHp);
      const eff = wounded.length > 0 ? wounded : pool;
      return pickByMinMax(eff, c => c.hp, false);
    }
    case 'random':
      return pool[rng.int(pool.length)];
    case 'healer':
      // Эвристика: целитель = карта с ability_text.trigger содержащим "лечит" или "heal".
      // На А.4.1 нет конкретной механики целителя — фолбэк на weakest.
      return pickByMinMax(pool, c => c.maxHp, false);
    // В.2.a: Лив Лайф — «самый раненый» по ТЕКУЩЕМУ hp (диздок 7.4).
    // 'lowest_current_hp' — фракционный дефолт; 'lowest_hp' — алиас, ставится
    // как priority_override у части юнитов Лив Лайф в xlsx (значение совпадает
    // с дефолтом фракции).
    case 'lowest_current_hp':
    case 'lowest_hp':
      return pickByMinMax(pool, c => c.hp, false);
    case 'weakest':
    default:
      return pickByMinMax(pool, c => c.maxHp, false);
  }
}

// Публичный helper «самый слабый юнит» (минимум по МАКС HP).
// Tie-breaker — порядок в массиве (для unitsOnField это line0[0..3]→line1[0..3]).
// Используется Танаки-passive (А.4.5-fix5).
export function pickWeakestUnit(units) {
  if (!units || units.length === 0) return null;
  return pickByMinMax(units, c => c.maxHp, false);
}

function pickByMinMax(arr, metric, max) {
  let best = arr[0], bestVal = metric(best);
  for (let i = 1; i < arr.length; i++) {
    const v = metric(arr[i]);
    if ((max && v > bestVal) || (!max && v < bestVal)) {
      best = arr[i];
      bestVal = v;
    }
  }
  return best;
}
